import React from "react";
import { useFormik } from "formik";
import {
  TextField,
  Button,
  Typography,
  FormControlLabel,
  Switch,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import * as Yup from "yup";
import { EapProgram, School } from "../../types/School";
import Grid from "@mui/material/Grid";
import AvatarUpload from "../AvatarUpload";
import { uploadAvatar } from "../../services/images";
import { closeSnackbar, enqueueSnackbar } from "notistack";

export interface SchoolEntryFormProps {
  school?: School;
  onSubmit: (values: School) => void;
}

export const SchoolEntryForm: React.FC<SchoolEntryFormProps> = ({
  school,
  onSubmit,
}) => {
  const formik = useFormik({
    initialValues: {
      ...{
        subscribedPrograms: Object.values(EapProgram),
        name: "",
        photoUrl: "",
        city: "",
        country: "",
        principalName: null,
        principalId: null,
        isActive: true,
      },
      ...school,
      principalName: school?.principalName || null,
      principalId: school?.principalId || null,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string()
        .max(100, "Must be 100 characters or less")
        .required("Required"),
      city: Yup.string()
        .max(50, "Must be 50 characters or less")
        .required("Required"),
      country: Yup.string()
        .max(50, "Must be 50 characters or less")
        .required("Required"),
      isActive: Yup.boolean().required("Required"),
    }),
    onSubmit: (values: School) => {
      // Create a copy of values to avoid directly mutating the state
      let submissionValues = { ...values };

      // Remove principalName and principalId if they are null or ""
      if (!submissionValues.principalId) {
        delete submissionValues.principalName;
        delete submissionValues.principalId;
      }

      onSubmit(submissionValues);
      formik.resetForm({ values: submissionValues as any });
    },
  });

  //const [subscribedPrograms, setPrograms] = React.useState(() => formik.values.subscribedPrograms);

  const handleSubscribedProgramChange = (
    event: React.MouseEvent<HTMLElement>,
    newSubscribedPrograms: EapProgram[]
  ) => {
    console.log(newSubscribedPrograms, "subscribedPrograms");
    if (newSubscribedPrograms.length) {
      // setPrograms(newSubscribedPrograms);
      formik.setFieldValue("subscribedPrograms", newSubscribedPrograms);
    }
  };

  const handleAvatarUpload = (file: File) => {
    uploadAvatar(file)
      .then((response) => {
        formik.setFieldValue("photoUrl", response.imageUrl);
      })
      .catch((error) => {
        enqueueSnackbar(`${error?.response?.data}`, {
          variant: "error",
          action: (key) => (
            <Button onClick={() => closeSnackbar(key)}>Dismiss</Button>
          ),
        });
      });
  };

  const handleAvatarRemove = () => {
    formik.setFieldValue("photoUrl", "");
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <AvatarUpload
            photoUrl={formik.values.photoUrl}
            logoText="School logo"
            onSubmit={(file: File) => handleAvatarUpload(file)}
            onRemove={() => handleAvatarRemove()}
          ></AvatarUpload>
        </Grid>

        <Grid item xs={12}>
          <TextField
            size="small"
            fullWidth
            autoComplete="off"
            id="name"
            name="name"
            label="School Name"
            value={formik.values.name}
            onChange={formik.handleChange}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            size="small"
            fullWidth
            autoComplete="off"
            id="city"
            name="city"
            label="City"
            value={formik.values.city}
            onChange={formik.handleChange}
            error={formik.touched.city && Boolean(formik.errors.city)}
            helperText={formik.touched.city && formik.errors.city}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            size="small"
            fullWidth
            autoComplete="off"
            id="country"
            name="country"
            label="Country"
            value={formik.values.country}
            onChange={formik.handleChange}
            error={formik.touched.country && Boolean(formik.errors.country)}
            helperText={formik.touched.country && formik.errors.country}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Subscribed Programs
          </Typography>

          <ToggleButtonGroup
            value={formik.values.subscribedPrograms}
            onChange={handleSubscribedProgramChange}
            aria-label="device"
          >
            <ToggleButton
              value={EapProgram.EAP_YELLOW}
              aria-label="YELLOW"
              style={{ color: "#000000" }}
            >
              YELLOW
            </ToggleButton>
            <ToggleButton
              value={EapProgram.EAP_GREEN}
              aria-label="GREEN"
              style={{ color: "#000000" }}
            >
              GREEN
            </ToggleButton>
            <ToggleButton
              value={EapProgram.EAP_ACCESS}
              aria-label="ACCESS"
              style={{ color: "#000000" }}
            >
              ACCESS
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={formik.values.isActive}
                onChange={(event) => {
                  formik.setFieldValue("isActive", event.target.checked);
                }}
              />
            }
            label={formik.values.isActive ? "Active" : "Disabled"}
            labelPlacement="start"
          />
        </Grid>
      </Grid>
      <Button
        color="primary"
        variant="contained"
        type="submit"
        style={{ marginTop: "2rem" }}
        disabled={!formik.dirty}
      >
        {formik.values._id ? "Update" : "Add new"} school
      </Button>
    </form>
  );
};
